import { isBefore, isSameDay } from "date-fns";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defineMessage, useIntl } from "react-intl";
import { FormValidation } from "react-jsonschema-form";
import { useHistory } from "react-router";
import { useRoleChecker, withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyInventoryData } from "../../components/inventory-provider";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { PropertyAdminDataProvider } from "../../components/property-admin-data-provider";
import actions from "../../lib/actions";
import { useApiClient } from "../../lib/http";
import { InventoryEntry } from "../../lib/model";
import { CALL_MOD_RESTRICTED_USER } from "../../lib/roles";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { ModificationPriceManagement } from "../shared/widgets/modificationPriceManagement/modificationPriceManagement";
import Section from "../shared/widgets/section";
import { FEATURE_TAGS } from "../witadmin/Common/constants";
import { FormContainer } from "./form-style";
import {
  buildGridSyncKey,
  getInvalidDailyValueDates,
  getUnratedStayDates,
} from "./dailyValuesDates";
import { BundleDTO, fromBundleDTOToRoomReservationBundle } from "./mapper";
import { Amount, DailyValue, Reservation, TransformedReservation } from "./model";
import schemaJSON from "./schema.json";

const messages = {
  ...defaultCrudMessages,
  entityName: {
    id: "modification.entityName",
    defaultMessage: "Edit booking",
  },
  dateMustBeAfter: {
    id: "modification.translation.dateMustBeAfter",
    defaultMessage: "The end date must be greater than the start date",
  },
  dailyValuesDatesMismatch: {
    id: "modification.translation.dailyValuesDatesMismatch",
    defaultMessage:
      "The daily prices dates do not match the check-in / check-out dates. Please review the stay dates and daily prices before saving.",
  },
  noRateLoadedForDates: {
    id: "modification.translation.noRateLoadedForDates",
    defaultMessage:
      "This booking cannot be saved: there are no rates loaded for the highlighted dates. Please contact the property so they can load them.",
  },
  mustNotBeZero: {
    id: "modification.translation.mustNotBeZero",
    defaultMessage: "All the prices must be higher than 0",
  },
  depositMustBeZeroOrMore: {
    id: "modification.translation.depositMustBeZeroOrMore",
    defaultMessage: "The deposit cannot be less than 0",
  },
  emailNotValid: {
    id: "modification.translation.emailNotValid",
    defaultMessage: "Please enter a valid email address",
  },
  phoneNotValid: {
    id: "modification.translation.phoneNotValid",
    defaultMessage: "Please enter a valid phone address",
  },
  notModifiable: {
    id: "modification.translation.notModifiable",
    defaultMessage: "This reservation is not modifiable, please contact our support team",
  },
  insuranceWarning: {
    id: "modification.translation.insuranceWarning",
    defaultMessage:
      "Esta reserva tiene un seguro asociado. Al modificar la reserva, el importe del seguro no se recalcula automáticamente. Revisar si los cambios afectan a la cobertura y, si es necesario, contactar con el proveedor del seguro<b>This reservation has an insurance policy associated.</b> When modifying the reservation, the insurance amount is not recalculated automatically. Please check if the changes affect the coverage and, if necessary, contact the insurance provider.",
  },
};

function extractRoomStayId() {
  const slashPLength = 9;
  const pathname = window?.location?.pathname;
  const pStart = pathname?.indexOf("/P");
  return pathname.substring(pStart + 1, pStart + 1 + slashPLength);
}

function ReservationModificationForm({ propertyTicker }: { propertyTicker: string }) {
  const httpClient = useApiClient();
  const inventories = usePropertyInventoryData();
  const intl = useIntl();
  const history = useHistory();

  const [schema, setSchema] = useState(schemaJSON);
  const [isPackage, setIsPackage] = useState(false);
  const [hasInsurance, setHasInsurance] = useState(false);

  const isRestrictedModificationUser = useRoleChecker([CALL_MOD_RESTRICTED_USER]);

  const getInventory = useCallback(
    (entity: TransformedReservation): InventoryEntry | undefined => {
      const inventoryTicker = entity.priceManagement?.inventoryTicker;

      if (!inventories?.[0]?.inventoryTickers) {
        return undefined;
      }

      if (!inventoryTicker) {
        throw new Error("No inventory ticker selected");
      }

      const inventory = inventories[0].inventoryTickers.find(({ ticker, virtualInventory }) => {
        if (virtualInventory.length > 0) {
          return virtualInventory.some(it => it.ticker === inventoryTicker);
        }
        return ticker === inventoryTicker;
      });

      if (!inventory) {
        return undefined;
      }

      const hasVirtualInventory =
        inventory.virtualInventory && inventory.virtualInventory.length > 0;
      if (hasVirtualInventory) {
        const virtualInv = inventory.virtualInventory.find(it => it.ticker === inventoryTicker);
        if (!virtualInv) {
          return undefined;
        }
        const [, configurationTicker, ,] = virtualInv.ticker.split("_");
        return {
          ...inventory,
          configuration: { ...inventory.configuration, ticker: configurationTicker },
        };
      }
      return inventory;
    },
    [inventories]
  );

  const allInventoryTickers = useMemo(() => {
    if (inventories?.length && inventories[0] && inventories[0].inventoryTickers) {
      return inventories[0].inventoryTickers.flatMap(
        ({ virtualInventory, ticker }: InventoryEntry) => {
          return virtualInventory.length > 0 ? virtualInventory.map(it => it.ticker) : [ticker];
        }
      );
    }
    return [];
  }, [inventories]);

  useEffect(() => {
    if (inventories?.length && inventories[0] && inventories[0].inventoryTickers) {
      const tempSchema: any = { ...schemaJSON };
      tempSchema.properties.priceManagement = {
        type: "object",
        properties: {
          dailyValues: {
            type: "array",
          },
          selectedServices: {
            type: "array",
          },
          quantity: {
            type: "number",
          },
          checkIn: { type: "string" },
          checkOut: { type: "string" },
          comments: { type: "string" },
          internalNotes: { type: "string" },
          inventoryTicker: {
            enum: allInventoryTickers,
            enumNames: inventories[0]?.inventoryTickers?.map(
              (inv: InventoryEntry) =>
                `${inv.accommodation.name} ${inv.configuration.name} ${inv.mealPlan.name} ${inv.condition.name} (${inv.ticker})`
            ),
          },
        },
      };
      setSchema(tempSchema);
    }
  }, [allInventoryTickers, inventories]);

  const onGet = useCallback(async () => {
    const pID = extractRoomStayId();
    const response = await httpClient.get<Reservation>(
      `/api/properties/${propertyTicker}/reservations/${pID}/masked`
    );
    if (
      response?.roomReservations[0]?.packageTicker ||
      response?.tags?.includes(FEATURE_TAGS.dynamic_packages)
    ) {
      setIsPackage(true);
    }

    if (response?.roomReservations[0]?.insurance) {
      setHasInsurance(true);
    }

    const transformedResponse: TransformedReservation = { ...response };

    if (transformedResponse) {
      transformedResponse.name = transformedResponse?.customer.name;
      transformedResponse.lastName = transformedResponse?.customer.lastName;
      transformedResponse.confirmationId = transformedResponse?.roomReservations[0].confirmationId;
      transformedResponse.email = transformedResponse?.customer.email;
      transformedResponse.country = transformedResponse?.customer.country;
      transformedResponse.phone = transformedResponse?.customer.phone;
      transformedResponse.inventoryTicker =
        transformedResponse?.roomReservations[0].inventoryTicker;

      transformedResponse.priceManagement = {
        dailyValues: Object.entries(transformedResponse?.roomReservations[0].dailyValues).reduce(
          (accum: DailyValue[], curr) => [
            ...accum,
            {
              ticker: curr[0],
              // Round the reconstructed applied price to 2 decimals: base + dailyDiscount is done in
              // JS floating point (e.g. 140.05 + (-14.00) = 126.05000000000001), so without this the
              // grid would show the raw noise as the applied/final price.
              amount: Number(
                (Object.keys(transformedResponse?.roomReservations[0].dailyDiscounts).length
                  ? (Number(Number(curr[1].amount).toFixed(2)) +
                      (transformedResponse?.roomReservations[0].dailyDiscounts[curr[0]] || 0)) /
                    transformedResponse?.roomReservations[0].quantity
                  : Number(Number(curr[1].amount).toFixed(2)) /
                    transformedResponse?.roomReservations[0].quantity
                ).toFixed(2)
              ),
            },
          ],
          []
        ),
        selectedServices: transformedResponse?.roomReservations[0].selectedServices,
        taxations: transformedResponse?.roomReservations[0].taxations,
        insurance: transformedResponse?.roomReservations[0].insurance,
        quantity: transformedResponse?.roomReservations[0].quantity,
        checkIn: transformedResponse?.roomReservations[0].checkIn,
        checkOut: transformedResponse?.roomReservations[0].checkOut,
        comments: transformedResponse?.roomReservations[0].comments,
        internalNotes: transformedResponse?.roomReservations[0].internalNotes,
        guaranteeAmount: Number(
          Number(transformedResponse?.roomReservations[0].guaranteeAmount.amount).toFixed(2)
        ),
        totalAmount: Number(
          Number(transformedResponse?.roomReservations[0].totalAmount.amount).toFixed(2)
        ),
        inventoryTicker: `${transformedResponse?.roomReservations[0].accommodationTicker}_${transformedResponse?.roomReservations[0].configurationTicker}_${transformedResponse?.roomReservations[0].mealPlanTicker}_${transformedResponse?.roomReservations[0].conditionTicker}`,
        originalPromoCodes: transformedResponse?.roomReservations[0].discounts,
        cancellationPolicy: transformedResponse?.roomReservations[0].cancellationPolicy,
        condition: transformedResponse?.roomReservations[0].condition,
        cancellationLastDay: transformedResponse?.cancellationLastDay,
        fullName: `${transformedResponse?.roomReservations[0].accommodationType}, ${transformedResponse?.roomReservations[0].mealPlan}, ${transformedResponse?.roomReservations[0].roomConfiguration}, ${transformedResponse?.roomReservations[0].condition.name}`,
      };
      if (transformedResponse?.priceManagement) {
        transformedResponse.priceManagement.originalDailyValues =
          transformedResponse?.priceManagement?.dailyValues;
      }
    }
    return transformedResponse;
  }, [httpClient, propertyTicker]);

  const onUpdate: (entity: TransformedReservation) => Promise<void> = useCallback(
    async entity => {
      const localEntity = cloneDeep(entity);

      localEntity.customer = {
        ...localEntity.customer,
        name: localEntity.name!!,
        lastName: localEntity.lastName!!,
        email: localEntity.email!!,
        country: localEntity.country!!,
        phone: localEntity.phone!!,
      };

      localEntity.roomReservations[0].comments = localEntity.priceManagement?.comments!!;
      localEntity.roomReservations[0].internalNotes = localEntity.priceManagement?.internalNotes!!;
      localEntity.roomReservations[0].guaranteeAmount.amount = localEntity.priceManagement?.guaranteeAmount!!;
      localEntity.roomReservations[0].inventoryTicker = localEntity.priceManagement?.inventoryTicker!!;
      localEntity.roomReservations[0].checkIn = localEntity.priceManagement?.checkIn || "";
      localEntity.roomReservations[0].checkOut = localEntity.priceManagement?.checkOut || "";
      localEntity.roomReservations[0].quantity = localEntity.priceManagement?.quantity!!;
      localEntity.roomReservations[0].totalAmount = {
        amount: localEntity.priceManagement?.totalAmount!!,
        currency: "EUR",
      };

      localEntity.roomReservations[0].dailyValues = localEntity.priceManagement?.dailyValues.reduce(
        (accum, curr) => ({
          ...accum,
          [curr.ticker as string]: {
            amount: curr.amount * localEntity.priceManagement?.quantity!!,
            currency: "EUR",
          },
        }),
        {}
      )!!;

      localEntity.roomReservations[0].selectedServices =
        localEntity?.priceManagement?.selectedServices || [];

      localEntity.roomReservations[0].selectedServices.forEach((item, index) => {
        localEntity.roomReservations[0].selectedServices[index].amount = {
          ...(localEntity.roomReservations[0].selectedServices[index].amount as Amount),
          amount:
            localEntity?.priceManagement?.selectedServices.find(it => it.ticker === item.ticker)
              ?.amount.amount || 0,
        };
      });

      const inventory = getInventory(localEntity);
      localEntity.roomReservations[0].accommodationTicker = inventory?.accommodation?.ticker || "";
      localEntity.roomReservations[0].configurationTicker = inventory?.configuration?.ticker || "";
      localEntity.roomReservations[0].mealPlanTicker = inventory?.mealPlan?.ticker || "";
      localEntity.roomReservations[0].conditionTicker = inventory?.condition?.ticker || "";

      localEntity.affiliate = entity.affiliate;
      localEntity.status = entity.status;

      delete localEntity.inventoryTicker;
      delete localEntity.datePicker;
      delete localEntity.confirmationId;
      delete localEntity.name;
      delete localEntity.lastName;
      delete localEntity.email;
      delete localEntity.priceManagement;
      delete localEntity.country;
      delete localEntity.phone;

      if (localEntity.roomReservations[0].bundle) {
        localEntity.roomReservations[0].bundle = fromBundleDTOToRoomReservationBundle(
          localEntity.roomReservations[0].bundle as BundleDTO
        );
      }
      await httpClient
        .put<Reservation, Reservation>(`/api/properties/admin/reservations`, localEntity)
        .then(() => {
          history.push(`/${propertyTicker}/reservations`);
        });
    },
    [getInventory, httpClient, history, propertyTicker]
  );

  const validate = useCallback(
    (formData: TransformedReservation, errors: FormValidation) => {
      const checkIn = formData.priceManagement
        ? new Date(formData.priceManagement.checkIn)
        : new Date();
      const checkOut = formData.priceManagement
        ? new Date(formData.priceManagement.checkOut)
        : new Date();

      // The daily values are rebuilt asynchronously after a date or inventory change, so they
      // are only meaningful once the grid has caught up with the form.
      const gridInSync =
        !!formData.priceManagement &&
        formData.priceManagement.gridSyncedFor ===
          buildGridSyncKey({
            checkIn: formData.priceManagement.checkIn,
            checkOut: formData.priceManagement.checkOut,
            inventoryTicker: formData.priceManagement.inventoryTicker,
          });

      if (isBefore(checkOut, checkIn) || isSameDay(checkOut, checkIn)) {
        errors.priceManagement.addError(intl.formatMessage(messages.dateMustBeAfter));
      } else if (
        gridInSync &&
        formData.priceManagement &&
        getInvalidDailyValueDates({
          checkIn: formData.priceManagement.checkIn,
          checkOut: formData.priceManagement.checkOut,
          dailyValues: formData.priceManagement.dailyValues,
          originalDailyValues: formData.priceManagement.originalDailyValues,
          userPricedDates: formData.priceManagement.userPricedDates,
          canEditPrices: !isRestrictedModificationUser,
        }).size > 0
      ) {
        // Block the save but don't render the default inline chip at the bottom of the
        // widget: the message and the red highlighting are shown contextually next to the
        // dates and the price grid inside ModificationPriceManagement. Attaching the error
        // to a non-rendered subfield keeps the form invalid (so the generic "form has
        // errors" still shows under the buttons) without duplicating the message.
        (errors.priceManagement as FormValidation).checkOut?.addError(
          intl.formatMessage(messages.dailyValuesDatesMismatch)
        );
      }

      if (
        formData.priceManagement &&
        getUnratedStayDates({
          checkIn: formData.priceManagement.checkIn,
          checkOut: formData.priceManagement.checkOut,
          unratedDates: formData.priceManagement.unratedDates,
        }).size > 0
      ) {
        (errors.priceManagement as FormValidation).checkOut?.addError(
          intl.formatMessage(messages.noRateLoadedForDates)
        );
      }

      if (formData.priceManagement && formData.priceManagement.guaranteeAmount < 0) {
        errors.priceManagement.addError(intl.formatMessage(messages.depositMustBeZeroOrMore));
      }

      // if email ends with * is a masked email and must not be validated.
      // http://emailregex.com/
      if (
        formData.email == null ||
        (!formData.email.endsWith("*") &&
          !formData.email.match(
            /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
          ))
      ) {
        errors.priceManagement.addError(intl.formatMessage(messages.emailNotValid));
      }

      // if phone starts with * is a masked phone and must not be validated
      if (
        formData.phone &&
        !formData.phone.startsWith("*") &&
        !formData.phone.match(/\+\d\d?\d?\d?\s[0-9]+/i)
      ) {
        errors.priceManagement.addError(intl.formatMessage(messages.phoneNotValid));
      }
      return errors;
    },
    [intl, isRestrictedModificationUser]
  );

  const allowedLocales = usePropertyLocales();
  const formConfiguration: FormConfiguration<TransformedReservation> = useMemo(
    () => ({
      __fieldOrder: [
        "guestInformationSection",
        "email",
        "name",
        "lastName",
        "phone",
        "bookingDetailsSection",
        "placementDate",
        "confirmationId",
        "*",
      ],
      __useProtoStyleArrays: true,
      id: {
        hidden: true,
      },
      ticker: {
        hidden: true,
      },
      checkIn: {
        hidden: true,
      },
      checkOut: {
        hidden: true,
      },
      guestInformationSection: {
        fieldWidget: Section,
        messages: {
          label: defineMessage({
            id: "modification.translation.guestInformationSection",
            defaultMessage: "Guest information",
          }),
        },
      },
      email: {
        messages: {
          label: defineMessage({
            id: "modification.translation.email",
            defaultMessage: "Description",
          }),
        },
      },
      name: {
        messages: {
          label: defineMessage({
            id: "modification.translation.name",
            defaultMessage: "Description",
          }),
        },
      },
      lastName: {
        messages: {
          label: defineMessage({
            id: "modification.translation.lastName",
            defaultMessage: "Description",
          }),
        },
      },
      phone: {
        messages: {
          label: defineMessage({
            id: "modification.translation.phone",
            defaultMessage: "Phone",
          }),
        },
      },
      bookingDetailsSection: {
        fieldWidget: Section,
        messages: {
          label: defineMessage({
            id: "modification.translation.bookingDetailsSection",
            defaultMessage: "Guest information",
          }),
        },
      },
      placementDate: {
        readonly: true,
        messages: {
          label: defineMessage({
            id: "modification.translation.placementDate",
            defaultMessage: "placementDate",
          }),
        },
      },
      confirmationId: {
        readonly: true,
        messages: {
          label: defineMessage({
            id: "modification.translation.confirmationId",
            defaultMessage: "confirmationId",
          }),
        },
      },
      priceManagement: {
        // TODO: This disabled is temporary until the modification of reservations with package improvement is implemented
        fieldWidget: props => (
          <PropertyAdminDataProvider loadInventories={true}>
            <ModificationPriceManagement
              {...props}
              disabled={isPackage || isRestrictedModificationUser}
              isRestrictedModification={isRestrictedModificationUser}
            />
          </PropertyAdminDataProvider>
        ),
        messages: {
          label: [
            defineMessage({
              id: "modification.translation.checkIn",
              defaultMessage: "checkIn",
            }),
            defineMessage({
              id: "modification.translation.checkOut",
              defaultMessage: "checkOut",
            }),
          ],
        },
      },
    }),
    [isPackage, isRestrictedModificationUser]
  );
  if (allowedLocales === undefined) {
    return null;
  }
  return (
    <CrudForm<TransformedReservation>
      formConfiguration={formConfiguration}
      schema={schema}
      container={FormContainer}
      id={"modification"}
      messages={messages}
      onGet={onGet}
      onUpdate={onUpdate}
      canUpdate={actions.ModifyReservation}
      validate={validate}
      warningMessage={hasInsurance ? messages.insuranceWarning : undefined}
    />
  );
}

export default withAuthorizationVerifier(ReservationModificationForm);
